import { FormikHelpers } from "formik";
import {
  OutagesResponse,
  OutageViewModel,
} from "../../../api/generated/models";
import {
  IFormValues,
  ISearchFormValues,
} from "../../../components/form/Form.types";
import { ELogTypes } from "../../../shared/enums/logging.enums";
import { IStandardError } from "../../../shared/types/error.types";
import { AppThunk } from "../../store";
import { DispatchLogger } from "../../utils/dispatch-logger/DispatchLogger";
import {
  setIsLoading,
  submitOutagesFailed,
  submitOutagesSuccess,
  getOutagesFailed,
  getOutagesSuccess,
  searchOutagesFailed,
  searchOutagesSuccess,
  adminOutagesFailed,
  adminOutagesSuccess,
  setIsAdminLoading,
  updateAdminOutagesFailed,
} from "./outages.slice";
import ErrorConstants from "../../../api/constants/error.constants";
import { ApiService } from "../../../api/api.service";
import Extent from "@arcgis/core/geometry/Extent";
import {
  buildSubmitPayload,
  getBracketTypeDisplayLabel,
  getLampTypeLabel,
} from "../../utils/state/outages.utils";
import { IGTMEventFunction } from "../../../shared/types/logging.types";
import {
  setLoading,
  setShowAdminModal,
  updateSearchView,
} from "../global/global.slice";
import { ESearchWizardSteps } from "../../../shared/enums/search.enums";
import { Dispatch, AnyAction } from "@reduxjs/toolkit";
import { isNullOrUndefined } from "../../../shared/utils/data.utils";
import {
  BlobServiceClient,
  BlockBlobUploadResponse,
} from "@azure/storage-blob";
import FormUtils from "../../../components/form/Form.utils";
import { BaseMapService } from "../../../components/map/BaseMap.service";
import Graphic from "@arcgis/core/Graphic";
import MapUtils from "../../../components/map/Map.utils";
import Point from "@arcgis/core/geometry/Point";

/**
 * --------------------------------------------------------------------------
 *  THUNKS
 * --------------------------------------------------------------------------
 */
export const fetchOutagesByExtent =
  (currentExtent: Extent): any =>
  async (dispatch: Dispatch<AnyAction>) => {
    dispatch(setIsLoading(true));
    const apiService: ApiService = ApiService.getInstance();
    const { outagesApi } = apiService;
    try {
      const results = (await outagesApi.getOutagesByExtent({
        currentExtent,
      })) as OutagesResponse;

      const dispatchLogger = DispatchLogger.getInstance(dispatch);

      if (results.isAxiosError || results.name === "Error") {
        const axiosError = results.toJSON();
        const error: IStandardError = {
          message: axiosError.message,
          code: axiosError.code,
          displayMessage: ErrorConstants.GENERIC_ERROR_MESSAGE,
        };

        dispatch(setLoading(false));
        dispatchLogger.fire(
          getOutagesFailed,
          { ...error },
          {
            message: "Fetch Outages Failed",
            type: ELogTypes.ERROR,
            data: { error },
          }
        );
      } else {
        dispatchLogger.fire(
          getOutagesSuccess,
          { ...results },
          {
            message: "Outages Fetched",
            type: ELogTypes.API,
            data: { results },
          }
        );
      }

      dispatch(setIsLoading(false));
      return results;
    } catch (error) {
      dispatch(getOutagesFailed(error));
      dispatch(setIsLoading(false));
      dispatch(setLoading(false));
    }
  };

/**
 * submitOutage
 * @param outage
 * @param formActions
 * @returns Promise<any>
 */
export const submitOutage =
  (
    formValues: IFormValues,
    formActions: FormikHelpers<IFormValues>,
    sendDataToGTM: IGTMEventFunction
  ): any =>
  async (
    dispatch: Dispatch<AnyAction>,
    getState: () => { globalState: any; outages: any }
  ) => {
    dispatch(setIsLoading(true));
    formActions.setSubmitting(true);

    const apiService: ApiService = ApiService.getInstance();
    const { outagesApi } = apiService;

    const { globalState, outages } = getState();

    const { ui } = globalState;
    const { pinMode } = ui;
    const { imageUpload } = outages;

    const payload = pinMode.enabled
      ? buildSubmitPayload(formValues, pinMode.pin)
      : buildSubmitPayload(formValues, ui.selectedMarker);

    const hasImage =
      !isNullOrUndefined(imageUpload.imgDataUrl) &&
      !isNullOrUndefined(imageUpload.fileData);

    let results: OutagesResponse;

    try {
      results = (await outagesApi.submitOutage(
        payload,
        hasImage,
        imageUpload.fileData
      )) as OutagesResponse;
    } catch (error) {
      dispatch(getOutagesFailed(error));
      formActions.setSubmitting(false);
    }

    const dispatchLogger = DispatchLogger.getInstance(dispatch);

    if (results.isAxiosError || results.name === "Error") {
      const axiosError = results.toJSON();
      const error: IStandardError = {
        message: axiosError.message,
        code: axiosError.code,
        displayMessage: ErrorConstants.GENERIC_ERROR_MESSAGE,
      };

      dispatchLogger.fire(
        submitOutagesFailed,
        { ...error },
        {
          message: "Outage Submit Failed",
          type: ELogTypes.ERROR,
          data: { error },
        }
      );

      sendDataToGTM({
        event: "eventTracking",
        category: "Outage",
        action: "Street Light",
        label: "Outage Submission Failed",
      });
    } else {
      formActions.resetForm();
      const { imageUploadUri } = results;

      if (!isNullOrUndefined(imageUploadUri)) {
        const { fileData } = imageUpload;

        const serviceClient = new BlobServiceClient(imageUploadUri);
        const containerClient = serviceClient.getContainerClient(".");

        const blockBlobClient = containerClient.getBlockBlobClient(
          `${results.outageId}.jpg`
        );

        const imageBlob = FormUtils.getBlobFromDataURI(imageUpload.imgDataUrl);

        blockBlobClient
          .upload(imageBlob, fileData.size)
          .then((response: BlockBlobUploadResponse) => {
            console.log("Blob Upload Complete:", response);
          })
          .catch((error) => {
            console.log(error);
          });
      }

      dispatchLogger.fire(
        submitOutagesSuccess,
        { ...results },
        {
          message: "Outage Submitted",
          type: ELogTypes.API,
          data: { results },
        }
      );

      const submittedLabel = pinMode.enabled
        ? "Outage Submitted - Pin Mode"
        : "Outage Submitted";

      sendDataToGTM({
        event: "eventTracking",
        category: "Outage",
        action: "Street Light",
        label: submittedLabel,
      });

      sendDataToGTM({
        event: "eventTracking",
        category: "Outage",
        action: "Street Light",
        label: `Outage Reason - ${results.outageReason}`,
      });
    }

    formActions.setSubmitting(false);
  };

export const searchOutages =
  (
    formValues: ISearchFormValues,
    formActions: FormikHelpers<ISearchFormValues>,
    sendDataToGTM: IGTMEventFunction
  ): any =>
  async (dispatch: Dispatch<AnyAction>, getState: any) => {
    dispatch(setIsLoading(true));
    formActions.setSubmitting(true);

    const dispatchLogger = DispatchLogger.getInstance(dispatch);
    const apiService: ApiService = ApiService.getInstance();
    const { outagesApi } = apiService;
    const { searchFormText } = formValues;

    try {
      const results = (await outagesApi.searchOutages(
        searchFormText
      )) as OutagesResponse;

      if (results.isAxiosError || results.name === "Error") {
        const axiosError = results.toJSON();
        const error: IStandardError = {
          message: axiosError.message,
          code: axiosError.code,
          displayMessage: ErrorConstants.GENERIC_ERROR_MESSAGE,
        };

        dispatchLogger.fire(
          searchOutagesFailed,
          { ...error },
          {
            message: "Outage Search Failed",
            type: ELogTypes.ERROR,
            data: { error },
          }
        );

        sendDataToGTM({
          event: "eventTracking",
          category: "Outage",
          action: "Street Light",
          label: "Outage Search Failed",
        });
      } else {
        formActions.resetForm();
        dispatchLogger.fire(
          searchOutagesSuccess,
          { results },
          {
            message: "Outage Search",
            type: ELogTypes.API,
            data: { results },
          }
        );

        sendDataToGTM({
          event: "eventTracking",
          category: "Outage",
          action: "Street Light",
          label: "Outage Look Up Search",
        });
      }

      formActions.setSubmitting(false);
      dispatch(updateSearchView(ESearchWizardSteps.RESULTS));
    } catch (error) {
      dispatch(searchOutagesFailed(error));
      formActions.setSubmitting(false);
    }
  };

export const fetchAdminOutages =
  (pageSize = 50, pageNumber = 1): any =>
  async (dispatch: Dispatch<AnyAction>, getState: any) => {
    dispatch(setIsAdminLoading(true));

    const dispatchLogger = DispatchLogger.getInstance(dispatch);
    const apiService: ApiService = ApiService.getInstance();
    const { outagesApi } = apiService;

    try {
      const results = (await outagesApi.searchAdminOutages(
        pageSize,
        pageNumber
      )) as OutagesResponse;

      if (results.isAxiosError || results.name === "Error") {
        const axiosError = results.toJSON();
        const error: IStandardError = {
          message: axiosError.message,
          code: axiosError.code,
          displayMessage: ErrorConstants.GENERIC_ERROR_MESSAGE,
        };

        dispatchLogger.fire(
          adminOutagesFailed,
          { ...error },
          {
            message: "Fetching Admin Outages Failed",
            type: ELogTypes.ERROR,
            data: { error },
          }
        );
      } else {
        dispatchLogger.fire(
          adminOutagesSuccess,
          { results },
          {
            message: "Admin Outages Fetched",
            type: ELogTypes.API,
            data: { results },
          }
        );
      }
    } catch (error) {
      dispatch(adminOutagesFailed(error));
    }
  };

export const updateAdminOutage =
  (
    selectedOutage: OutageViewModel,
    baseMapService: BaseMapService,
    sendDataToGTM: any,
    gisFeature?: Graphic,
    locationId?: number,
    completedReason?: string
  ): any =>
  async (dispatch: Dispatch<AnyAction>, getState: any) => {
    dispatch(setIsAdminLoading(true));

    const dispatchLogger = DispatchLogger.getInstance(dispatch);
    const apiService: ApiService = ApiService.getInstance();
    const { outagesApi } = apiService;

    const { outageId } = selectedOutage;

    let results;
    let mergedPayload: OutageViewModel = selectedOutage;

    if (gisFeature) {
      const { geometry, attributes } = gisFeature;
      const mapPoint = new Point({
        latitude: attributes.LATITUDE,
        longitude: attributes.LONGITUDE,
      });
      const geoLocResponse = await MapUtils.fetchGeoLocData(mapPoint);
      const xStreetsResponse = await MapUtils.fetchCrossStreets(geometry);
      const { geoLocPayload } = MapUtils.buildGeoLocPayload(
        geoLocResponse,
        xStreetsResponse
      );
      mergedPayload = {
        ...selectedOutage,
        location: {
          ...selectedOutage.location,
          county: attributes.COUNTY,
          contractAccountNumber: attributes.CONTRACT_A,
          coordinates: {
            ...selectedOutage?.location?.coordinates,
            latitude: attributes.LATITUDE,
            longitude: attributes.LONGITUDE,
          },
          locationId: attributes.GISID,
          township: attributes.TOWNSHIP,
          trs: attributes.TRS,
          //Address unknown occurance should not occur unless crossstreet is null
          address: geoLocPayload.address?.Address || "Address Unknown",
          crossStreets: geoLocPayload.crossStreets,
        },
        utility: {
          ...selectedOutage.utility,
          lampType: getLampTypeLabel(attributes.LAMPTYPE),
          bracketType: getBracketTypeDisplayLabel(attributes.FIXTURESUP),
          wattage: attributes.WATTAGE.toString(),
        },
      };
    }

    try {
      // @todo:  Still need to send the correct obj/values to the back-end.
      // If we are accepting, send mergedPayload. Otherwise send outageId,
      // locationId, and completedReason.
      if (outageId) {
        results = (await outagesApi.updateAdminOutage(
          mergedPayload,
          locationId,
          completedReason
        )) as OutagesResponse;
      }

      if (results.isAxiosError || results.name === "Error") {
        const axiosError = results.toJSON();
        const error: IStandardError = {
          message: axiosError.message,
          code: axiosError.code,
          displayMessage: ErrorConstants.GENERIC_ERROR_MESSAGE,
        };

        dispatchLogger.fire(
          updateAdminOutagesFailed,
          { ...error },
          {
            message: "Updating Admin Outage Failed",
            type: ELogTypes.ERROR,
            data: { error },
          }
        );

        if (locationId) {
          sendDataToGTM({
            event: "eventTracking",
            category: "Outage",
            action: "Street Light",
            label: "Admin Outage Accept Failed",
          });
        } else {
          sendDataToGTM({
            event: "eventTracking",
            category: "Outage",
            action: "Street Light",
            label: "Admin Outage Reject Failed",
          });
        }
      } else {
        const featureLayerService = baseMapService.getFeatureLayerService();
        dispatchLogger.fire(
          fetchAdminOutages,
          { results },
          {
            message: "Admin Outages Fetched",
            type: ELogTypes.API,
            data: { results },
          }
        );

        featureLayerService.removeNonCEGraphics();
        baseMapService.processExtent();

        dispatch(setIsAdminLoading(false));
        dispatch(setShowAdminModal({ show: false }));

        if (locationId) {
          sendDataToGTM({
            event: "eventTracking",
            category: "Outage",
            action: "Street Light",
            label: `Admin Outage Accepted - Outage ID: ${outageId}`,
          });
        } else if (completedReason) {
          sendDataToGTM({
            event: "eventTracking",
            category: "Outage",
            action: "Street Light",
            label: `Admin Outage Rejected - Outage ID: ${outageId} Rejected Reason: ${completedReason} `,
          });
        }
      }
    } catch (error) {
      dispatch(adminOutagesFailed(error));
    }
  };
